import { t } from '@lingui/core/macro';
import { VStack } from '@chakra-ui/react';
import { CheckCircle2, Hourglass, PauseCircle, PlayCircle, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';

import {
  getWorkPlacementGroups,
  type WorkPlacementGroupKey,
} from '../sections/shared/workPlacementStatusGroups';
import ContentContainer from './shared/ContentContainer';
import SidePanelContentHeader from './shared/ContentHeader';
import NoDataMessage from './shared/NoDataMessage';
import WorkPlacementStatusCard from './shared/WorkPlacementStatusCard';

const groupIcons: Record<WorkPlacementGroupKey, LucideIcon> = {
  onHold: PauseCircle,
  sourcing: Search,
  active: PlayCircle,
  awaitingEvidence: Hourglass,
  complete: CheckCircle2,
};

function WorkPlacementsWithData() {
  const { filters } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getWorkPlacementsBySemester.useQuery(
    { filters },
    { suspense: true },
  );

  if (data === undefined || data.length === 0) {
    return <NoDataMessage />;
  }

  const groups = getWorkPlacementGroups();

  return (
    <>
      <SidePanelContentHeader
        title={t`Course progress`}
        tooltipText={t`Placement status by semester and theory completion`}
      />

      <VStack spacing={2} align="stretch">
        {data.map((bucket) => {
          const label = bucket.theoryComplete
            ? t`Semester ${bucket.semester} complete`
            : t`Semester ${bucket.semester} in progress`;

          const rows = groups.map((group) => ({
            key: group.key,
            label: group.label,
            icon: groupIcons[group.key],
            colour: group.backgroundColour,
            count:
              group.key === 'onHold'
                ? bucket.onHoldCount
                : group.statuses.reduce(
                    (total, status) =>
                      total + (bucket.statusCounts.find((s) => s.status === status)?.count ?? 0),
                    0,
                  ),
          }));

          // Sum the rows we actually show so the total always reconciles. Statuses not
          // surfaced in any group (e.g. failed/cancelled) are deliberately excluded.
          const total = rows.reduce((sum, row) => sum + row.count, 0);

          return (
            <WorkPlacementStatusCard
              key={`${bucket.semester}-${bucket.theoryComplete}`}
              title={label}
              rows={rows}
              total={total}
            />
          );
        })}
      </VStack>
    </>
  );
}

export default function WorkPlacements() {
  return (
    <ContentContainer title={t`Work placements`}>
      <WorkPlacementsWithData />
    </ContentContainer>
  );
}
